import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Play, Trash2, Loader2, Mic, Calendar, Pause
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MySpeakingRecordings = () => {
  const navigate = useNavigate();
  const [recordings, setRecordings] = useState([]);
  const [byLevel, setByLevel] = useState({});
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  useEffect(() => {
    fetchRecordings();
  }, []);
  
  const fetchRecordings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/skill-tests/speaking/my-recordings`,
        { withCredentials: true }
      );
      setRecordings(response.data.recordings || []);
      setByLevel(response.data.by_level || {});
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setRecordings([]);
      setByLevel({});
    } finally {
      setLoading(false);
    }
  };
  
  const playRecording = (recordingId, audioData) => {
    const existingAudio = document.querySelector('.playing-audio');
    if (existingAudio) {
      existingAudio.pause();
      existingAudio.remove();
    }
    
    if (playingId === recordingId) {
      setPlayingId(null);
      return;
    }
    
    const audio = new Audio(audioData);
    audio.className = 'playing-audio';
    audio.style.display = 'none';
    document.body.appendChild(audio);
    
    audio.onended = () => {
      setPlayingId(null);
      audio.remove();
    };
    
    audio.play();
    setPlayingId(recordingId);
  };
  
  const deleteRecording = async (recordingId) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    
    setDeletingId(recordingId);
    try {
      await axios.delete(
        `${API_URL}/api/skill-tests/speaking/recording/${recordingId}`,
        { withCredentials: true }
      );
      setRecordings(recordings.filter(r => r.id !== recordingId));
      
      const newByLevel = {};
      for (const [level, recs] of Object.entries(byLevel)) {
        newByLevel[level] = recs.filter(r => r.id !== recordingId);
        if (newByLevel[level].length === 0) delete newByLevel[level];
      }
      setByLevel(newByLevel);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Kayıt silinemedi');
    } finally {
      setDeletingId(null);
    }
  };
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const levelNames = {
    'A0': 'A0 - Absolute Beginner',
    'A1': 'A1 - Beginner',
    'A2': 'A2 - Elementary',
    'B1': 'B1 - Intermediate',
    'B2': 'B2 - Upper Intermediate',
    'C1': 'C1 - Advanced'
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mavi Gradient (KariyerLevel ile aynı) */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <Mic className="w-8 h-8 text-rose-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Speaking Alıştırmalarım</h1>
              <p className="text-rose-100 text-sm">Tüm ses kayıtlarınız burada</p>
              <p className="text-rose-200 text-xs mt-1">
                {recordings.length} kayıt • {Object.keys(byLevel).length} seviye
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 -mt-4">
        {recordings.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mic className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">Henüz kayıt yok</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Speaking testlerinde ses kaydı yaparak burada dinleyebilir ve ilerlemenizi takip edebilirsiniz.
              </p>
              <Button 
                onClick={() => navigate('/english-training')}
                className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
              >
                <Mic className="w-4 h-4 mr-2" />
                Speaking Testine Başla
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-rose-50 to-pink-50">
              <CardContent className="p-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-rose-600">{recordings.length}</p>
                    <p className="text-sm text-gray-600">Toplam Kayıt</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-rose-600">{Object.keys(byLevel).length}</p>
                    <p className="text-sm text-gray-600">Seviye</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Recordings by Level */}
            {Object.entries(byLevel).sort().map(([level, levelRecordings]) => (
              <div key={level}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-4 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full text-sm font-semibold">
                    {level}
                  </span>
                  <span className="text-gray-600 text-sm">
                    {levelNames[level] || level}
                  </span>
                  <span className="text-gray-400 text-sm">
                    ({levelRecordings.length} kayıt)
                  </span>
                </div>
                
                <div className="space-y-3">
                  {levelRecordings.map((recording) => (
                    <Card key={recording.id} className="border-0 shadow-md hover:shadow-lg transition-all">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Play Button */}
                          <button
                            onClick={() => playRecording(recording.id, recording.audio_data)}
                            className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              playingId === recording.id
                                ? 'bg-rose-500 animate-pulse'
                                : 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600'
                            }`}
                          >
                            {playingId === recording.id ? (
                              <Pause className="w-6 h-6 text-white" />
                            ) : (
                              <Play className="w-6 h-6 text-white ml-1" />
                            )}
                          </button>
                          
                          {/* Question Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 mb-1 line-clamp-2">
                              {recording.question_text}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{formatDate(recording.created_at)}</span>
                            </div>
                          </div>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => deleteRecording(recording.id)}
                            disabled={deletingId === recording.id}
                            className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                          >
                            {deletingId === recording.id ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Trash2 className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySpeakingRecordings;
